import { useCallback } from "react";
import { Scene } from "@babylonjs/core/scene";
import { MAP_MESH_SIZE, BUTTON_ASSET_SIZE } from "common/model";
import { FORWARD_WORLD, GameState } from "./types";
import { performCharJump, performCharPlaceJump, performCharFall } from "../../animations";
import { probeFront, probeButtonAt, calcOffset, isCeilingBlocked, RAY_DISTANCE_JUMP } from "../ray";

type Deps = {
  stateRef: React.MutableRefObject<GameState | null>;
  sceneRef: React.MutableRefObject<Scene | null>;
  speedRef: React.MutableRefObject<number>;
  success: boolean;
  setAnimating: (v: boolean) => void;
  resetCharToStart: () => void;
  tryTriggerClear: () => void;
  jumpSound: (rate?: number) => void;
  failureSound: (rate?: number) => void;
  fallingSound: (rate?: number) => void;
};

export const useHandleJump = (deps: Deps) => {
  const {
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    resetCharToStart,
    tryTriggerClear,
    jumpSound,
    failureSound,
    fallingSound,
  } = deps;

  return useCallback(async () => {
    const g = stateRef.current;
    const scene = sceneRef.current;
    if (!g || !scene || !g.charMesh || g.isAnimating || success) return;
    const char = g.charMesh;
    const fwd = FORWARD_WORLD[g.charDir];
    const speed = speedRef.current;

    // 現在ボタン上か → 実効床面 Y 補正
    const currentBtn = probeButtonAt(scene, char.position.x, char.position.y, char.position.z, 1);
    const effectiveCharY = char.position.y - (currentBtn ? BUTTON_ASSET_SIZE.y : 0);

    // 前 1 マス（distance 3.5: drop も検出）
    const probe1 = probeFront(scene, char.position, fwd, effectiveCharY, RAY_DISTANCE_JUMP);

    // 前マスのボタン
    const target1X = char.position.x + fwd.dx * MAP_MESH_SIZE;
    const target1Z = char.position.z + fwd.dz * MAP_MESH_SIZE;
    const nextBtn1 = probeButtonAt(scene, target1X, char.position.y + 5, target1Z, 15);
    const offset1 = calcOffset(currentBtn, nextBtn1);

    g.isAnimating = true;
    setAnimating(true);
    try {
      // 着地予定マスの上空塞判定
      // step/drop の場合は前マスの topY、box の場合は probe1.topY、それ以外は char.y
      const targetY =
        probe1.kind === "step" || probe1.kind === "drop"
          ? probe1.topY + offset1
          : probe1.kind === "box"
            ? probe1.topY + offset1
            : char.position.y + offset1;

      const ceilingBlocked = isCeilingBlocked(scene, target1X, target1Z, targetY);
      const charTarget = { x: target1X, y: targetY, z: target1Z };

      switch (probe1.kind) {
        case "void":
          // 虚無 → 弧を描いて落下 → リセット
          jumpSound(speed);
          fallingSound(speed);
          await performCharFall(scene, char, fwd, { jumpArc: true }, speed);
          resetCharToStart();
          return;

        case "river":
          // 海 → 弧を描いて海上停止 → リセット
          jumpSound(speed);
          fallingSound(speed);
          await performCharFall(scene, char, fwd, { stopAtY: probe1.topY, jumpArc: true }, speed);
          resetCharToStart();
          return;

        case "wall":
          // 大壁 → 原地ジャンプ + ぶつかったマーク
          failureSound(speed);
          await performCharPlaceJump(scene, char, { withMark: true }, speed);
          return;

        case "floor":
          // 同高さ → 原地ジャンプ（common 一致）
          // ただし前マスにボタンがあれば実質 step 相当 → 前ジャンプ
          if (offset1 !== 0) {
            if (ceilingBlocked) {
              failureSound(speed);
              await performCharPlaceJump(scene, char, { withMark: true }, speed);
            } else {
              jumpSound(speed);
              await performCharJump(scene, char, charTarget, speed);
            }
          } else {
            jumpSound(speed);
            await performCharPlaceJump(scene, char, undefined, speed);
          }
          return;

        case "step":
        case "drop":
        case "box":
          if (ceilingBlocked) {
            failureSound(speed);
            await performCharPlaceJump(scene, char, { withMark: true }, speed);
          } else {
            jumpSound(speed);
            await performCharJump(scene, char, charTarget, speed);
          }
          return;
      }
    } finally {
      g.isAnimating = false;
      setAnimating(false);
      tryTriggerClear();
    }
  }, [
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    resetCharToStart,
    tryTriggerClear,
    jumpSound,
    failureSound,
    fallingSound,
  ]);
};
